import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { client } from '../amplify-client';
import { EisdielenKarte, type KartenPunkt } from '../components/EisdielenKarte';
import { GuennieRating } from '../components/GuennieRating';
import { KriteriumAnzeige } from '../components/KriteriumAnzeige';
import { SpaghettiDivider } from '../components/SpaghettiDivider';
import { formatDatum } from '../lib/format';
import { ALLE_KRITERIEN, AVG_FELD, KRITERIUM_LABEL, type Kriterium } from '../lib/kriterien';
import type { Schema } from '../../amplify/data/resource';

type Eisdiele = Schema['Eisdiele']['type'];
type Test = Schema['SpaghettiEisTest']['type'];

export function EisdieleDetail() {
  const { id } = useParams<{ id: string }>();
  const [eisdiele, setEisdiele] = useState<Eisdiele | null>(null);
  const [tests, setTests] = useState<Test[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLaedt(true);
    Promise.all([
      client.models.Eisdiele.get({ id }),
      client.queries.listeVeroeffentlichteTests({ eisdieleId: id, limit: 200 }),
    ])
      .then(([e, t]) => {
        if (!e.data) {
          setFehler('Eisdiele nicht gefunden.');
          return;
        }
        setEisdiele(e.data);
        setTests(((t.data ?? []).filter(Boolean) as Test[]).sort((a, b) => (b.besuchsdatum ?? '').localeCompare(a.besuchsdatum ?? '')));
      })
      .catch((e) => setFehler(String(e?.message ?? e)))
      .finally(() => setLaedt(false));
  }, [id]);

  // Referenzstabiles Array für EisdielenKarte (siehe deren Kommentar: bei
  // jeder neuen Array-Referenz baut die Karte sich komplett neu auf).
  const punkte = useMemo<KartenPunkt[]>(() => {
    if (!eisdiele) return [];
    return [
      {
        id: eisdiele.id,
        name: eisdiele.name,
        ort: eisdiele.ort,
        lat: eisdiele.lat,
        lng: eisdiele.lng,
        anzahlTests: eisdiele.anzahlTests ?? 0,
        avgGesamtGuennies: eisdiele.avgGesamtGuennies,
      },
    ];
  }, [eisdiele]);

  if (laedt) return <div className="container">Lädt …</div>;
  if (fehler || !eisdiele) {
    return (
      <div className="container">
        <p className="hinweis">{fehler ?? 'Nicht gefunden.'}</p>
        <Link to="/app/eisdielen">← zurück zur Übersicht</Link>
      </div>
    );
  }

  const osmLink = `https://www.openstreetmap.org/?mlat=${eisdiele.lat}&mlon=${eisdiele.lng}#map=17/${eisdiele.lat}/${eisdiele.lng}`;
  // (0,0) ist nie eine echte Position (siehe EisdielenKarte.tsx) — kommt bei
  // ein paar Alt-Datensätzen vor, die vor der Pflicht-Geokodierung entstanden.
  const hatGueltigePosition = eisdiele.lat !== 0 || eisdiele.lng !== 0;

  return (
    <div className="container" style={{ maxWidth: '46rem' }}>
      <p className="label">
        <Link to="/app/eisdielen">← Eisdielen</Link>
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ marginBottom: '0.2rem' }}>{eisdiele.name}</h1>
          <p style={{ color: 'var(--espresso-70)' }}>
            {eisdiele.strasse}, {eisdiele.plz} {eisdiele.ort}
            {eisdiele.region ? ` · ${eisdiele.region}` : ''}
          </p>
          {eisdiele.website ? (
            <p>
              <a href={eisdiele.website} target="_blank" rel="noreferrer noopener nofollow">
                Website
              </a>
            </p>
          ) : null}
          {hatGueltigePosition ? (
            <p>
              <a href={osmLink} target="_blank" rel="noreferrer noopener">
                Lage auf OpenStreetMap ansehen
              </a>
            </p>
          ) : null}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="label">Ø Gesamt</div>
          <GuennieRating wert={eisdiele.avgGesamtGuennies ?? null} groesse="lg" />
          <div className="label" style={{ marginTop: '0.35rem' }}>
            {eisdiele.anzahlTests ?? 0} Verkostung{(eisdiele.anzahlTests ?? 0) === 1 ? '' : 'en'}
          </div>
        </div>
      </div>

      {hatGueltigePosition ? (
        <EisdielenKarte punkte={punkte} hoehe={220} />
      ) : (
        <p className="hinweis">Für diese Eisdiele ist noch keine Position hinterlegt.</p>
      )}

      <SpaghettiDivider />

      <h2>Kriterien-Durchschnitte</h2>
      <div className="karte" style={{ marginBottom: '1.5rem' }}>
        {ALLE_KRITERIEN.map((k) => (
          <KriteriumAnzeige
            key={k}
            label={KRITERIUM_LABEL[k as Kriterium]}
            wert={eisdiele[AVG_FELD[k as Kriterium] as keyof Eisdiele] as number | null}
          />
        ))}
      </div>

      <h2>Verkostungen hier</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.6rem' }}>
        {tests.map((t) => (
          <Link
            key={t.id}
            to={`/app/verkostungen/${t.id}`}
            className="karte"
            style={{
              textDecoration: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>{formatDatum(t.besuchsdatum)}</div>
              <div style={{ color: 'var(--espresso-70)', fontSize: '0.9rem' }}>von {t.testerName}</div>
            </div>
            <GuennieRating wert={t.gesamtGuennies} groesse="sm" />
          </Link>
        ))}
        {tests.length === 0 ? <p style={{ color: 'var(--espresso-70)' }}>Noch keine veröffentlichte Verkostung.</p> : null}
      </div>
    </div>
  );
}
